// Деньги: работа только в минорных единицах (центах), без float-хвостов.
// Справочник валют пользовательский, поэтому символ и курс всегда приходят
// параметром — захардкоженной таблицы валют здесь нет.
import type { Currency, CurrencyDef, Money } from '../types';
import { BASE_CURRENCY } from '../types';

/**
 * Парсит число или строку в минорные единицы (центы) с округлением half-up.
 * Мусор (нечисловые строки, пустая строка, NaN/Infinity) -> 0.
 */
export function toMinor(value: number | string): number {
  const str = typeof value === 'number' ? numberToDecimalString(value) : value;
  if (str === undefined) return 0;
  return parseDecimalToMinor(str);
}

/** Минорные единицы -> число с плавающей точкой (для отображения/редактирования). */
export function fromMinor(minor: number): number {
  return minor / 100;
}

export function money(minor: number, c: Currency): Money {
  return { amount: minor, currency: c };
}

export function findCurrency(
  code: Currency,
  currencies: readonly CurrencyDef[],
): CurrencyDef | undefined {
  return currencies.find((c) => c.code === code);
}

/**
 * Курс валюты к базовой. Неизвестная валюта или мусорный курс дают 1: это
 * заведомо неверная цифра, но она не роняет расчёт всего варианта в NaN.
 */
export function rateToBase(code: Currency, currencies: readonly CurrencyDef[]): number {
  if (code === BASE_CURRENCY) return 1;
  const rate = findCurrency(code, currencies)?.rateToBase;
  return Number.isFinite(rate) && (rate as number) > 0 ? (rate as number) : 1;
}

/** Символ валюты; если его не задали, показываем сам код ('GBP 10.00'). */
export function currencySymbol(code: Currency, currencies: readonly CurrencyDef[]): string {
  const symbol = findCurrency(code, currencies)?.symbol;
  return symbol ? symbol : `${code} `;
}

/** '$153.40' / '€153.40' / 'GBP 153.40' / отрицательные '-$5.00'. */
export function formatMoney(
  minor: number,
  c: Currency,
  currencies: readonly CurrencyDef[],
): string {
  const symbol = currencySymbol(c, currencies);
  const negative = minor < 0;
  const abs = Math.abs(minor);
  const whole = Math.trunc(abs / 100);
  const cents = abs % 100;
  const value = `${whole}.${String(cents).padStart(2, '0')}`;
  return `${negative ? '-' : ''}${symbol}${value}`;
}

/**
 * Переводит минорные единицы Money в валюту `to` через базовую валюту.
 * Округление half-up, без -0.
 */
export function convertMinor(
  m: Money,
  to: Currency,
  currencies: readonly CurrencyDef[],
): number {
  if (m.currency === to) return m.amount;
  const inBase = m.amount * rateToBase(m.currency, currencies);
  const raw = inBase / rateToBase(to, currencies);
  const rounded = Math.round(raw);
  return rounded === 0 ? 0 : rounded; // защита от -0
}

/** Число -> строка с фиксированной точкой, без экспоненциальной записи. */
function numberToDecimalString(value: number): string | undefined {
  if (!Number.isFinite(value)) return undefined;
  return value.toFixed(6);
}

/** Строка десятичного числа -> минорные единицы, округление half-up к 2 знакам. */
function parseDecimalToMinor(input: string): number {
  const trimmed = input.trim();
  const m = /^(-)?(\d*)(?:[.,](\d+))?$/.exec(trimmed);
  if (!m) return 0;
  const [, signPart, intPartRaw, fracPartRaw] = m;
  if (!intPartRaw && !fracPartRaw) return 0; // ни одной цифры

  const sign = signPart ? -1 : 1;
  const intPart = intPartRaw || '0';
  const frac = fracPartRaw ?? '';
  const fracPadded = (frac + '000').slice(0, 3);
  let cents = parseInt(fracPadded.slice(0, 2), 10);
  const roundDigit = fracPadded.charCodeAt(2) - 48;

  let intNum = parseInt(intPart, 10);
  if (roundDigit >= 5) {
    cents += 1;
    if (cents === 100) {
      cents = 0;
      intNum += 1;
    }
  }

  const minor = intNum * 100 + cents;
  return sign * minor;
}
